import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { numberObjectKeys } from '~/_shared/utils/object/objectEnumerators';
import {
  getSpreadsheetBulkData, type SpreadsheetDataBulkRequestGetter,
} from '~/spreadsheet/spreadsheet.repository';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { getLocationCountColumnNamesBySpreadsheet } from '~/store/stats/chartStats.helpers';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import {
  type Chart, ChartMetricType, isChartBoundaryStatistics, isChartMetricDemographic, isChartMetricLocationCount,
  isChartMetricSpreadsheetColumn,
} from '../mapSettings/data/charts/mapSettingsCharts.types';
import { addBoundaryStatsItems } from './stats.actionCreators';
import {
  createEmptyBoundaryStatItemDemographic, createEmptyBoundaryStatItemLocationCount,
  createEmptyBoundaryStatItemSpreadsheetColumn,
} from './stats.factory';
import {
  findBoundaryGroupStatItemDemographic, findBoundaryGroupStatItemLocationCount,
  findBoundaryGroupStatItemSpreadsheetColumn, type GroupColumnFormattedValuesLookup, parseFormattedValuesResponse,
} from './stats.helpers';
import { useBoundaryGroupStatsSelector } from './stats.selectors';
import type { BoundaryStatsItem } from './stats.state';

export const useChartsStats = (charts: ReadonlyArray<Chart>) => {
  const [groupValueLabelsLookup, setGroupValueLabelsLookup] = useState<GroupColumnFormattedValuesLookup>(new Map());
  const [areGroupValueLabelsLoading, setAreGroupValueLabelsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const boundaryGroupsStats = useBoundaryGroupStatsSelector();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();

  const getSpreadsheetColumnBoundaryStats = useCallback(({
    boundaryGroupId,
    spreadsheetColumnId,
  }: {
    boundaryGroupId: BoundaryGroupId;
    spreadsheetColumnId: SpreadsheetColumnId;
  }): { item: BoundaryStatsItem; isCached: boolean } => {
    const existingStatsItem = findBoundaryGroupStatItemSpreadsheetColumn(boundaryGroupsStats, {
      boundaryGroupId,
      type: ChartMetricType.SpreadsheetColumn,
      column: spreadsheetColumnId,
    });

    return {
      item: existingStatsItem || createEmptyBoundaryStatItemSpreadsheetColumn({
        boundaryGroupId,
        column: spreadsheetColumnId,
      }),
      isCached: !!existingStatsItem,
    };
  }, [boundaryGroupsStats]);

  const getLocationCountBoundaryStats = useCallback(({
    boundaryGroupId,
    column,
    columnValue,
    spreadsheetId,
  }: {
    boundaryGroupId: BoundaryGroupId;
    column?: SpreadsheetColumnId;
    columnValue?: string;
    spreadsheetId?: SpreadsheetId;
  }): { item: BoundaryStatsItem; isCached: boolean } => {
    const existingStatsItem = findBoundaryGroupStatItemLocationCount(boundaryGroupsStats, {
      boundaryGroupId,
      type: ChartMetricType.LocationCount,
      column,
      columnValue,
      spreadsheetId,
    });

    return {
      item: existingStatsItem || createEmptyBoundaryStatItemLocationCount({
        boundaryGroupId,
        column,
        columnValue,
        spreadsheetId,
      }),
      isCached: !!existingStatsItem,
    };
  }, [boundaryGroupsStats]);

  const getDemographicBoundaryStats = useCallback(({
    boundaryGroupId,
    demographicId,
  }: {
    boundaryGroupId: BoundaryGroupId;
    demographicId: string;
  }): { item: BoundaryStatsItem; isCached: boolean } => {
    const existingStatsItem = findBoundaryGroupStatItemDemographic(boundaryGroupsStats, {
      boundaryGroupId,
      type: ChartMetricType.Demographic,
      demographicId,
    });

    return {
      item: existingStatsItem || createEmptyBoundaryStatItemDemographic({
        boundaryGroupId,
        demographicId,
      }),
      isCached: !!existingStatsItem,
    };
  }, [boundaryGroupsStats]);

  const chartsStats = useMemo(() => charts.map((chart) => {
    if (isChartBoundaryStatistics(chart)) {
      return chart.metrics.map((metric) => {
        if (isChartMetricSpreadsheetColumn(metric)) {
          return getSpreadsheetColumnBoundaryStats({
            boundaryGroupId: chart.boundaryGroupId,
            spreadsheetColumnId: metric.column,
          }).item;
        }
        if (isChartMetricLocationCount(metric)) {
          return getLocationCountBoundaryStats({
            boundaryGroupId: chart.boundaryGroupId,
            column: metric.column,
            columnValue: metric.columnValue,
            spreadsheetId: metric.spreadsheetId,
          }).item;
        }
        if (isChartMetricDemographic(metric)) {
          return getDemographicBoundaryStats({
            boundaryGroupId: chart.boundaryGroupId,
            demographicId: metric.demographicId,
          }).item;
        }
        return null;
      });
    }
    return null;
  }), [charts, getDemographicBoundaryStats, getLocationCountBoundaryStats, getSpreadsheetColumnBoundaryStats]);

  const fetchGroupValueLabels = useCallback(async () => {
    if (!mapId || !clientId) {
      return;
    }

    const columnsBySpreadsheet = getLocationCountColumnNamesBySpreadsheet(charts);
    const spreadsheetIds = numberObjectKeys(columnsBySpreadsheet);

    if (!spreadsheetIds.length) {
      return;
    }

    const params: SpreadsheetDataBulkRequestGetter[] = spreadsheetIds.map(spreadsheetId => ({
      map_id: mapId,
      spreadsheet_id: spreadsheetId,
      exclude_basic_data: true,
      exclude_row_data: true,
      columns_to_fetch: {
        [DataType.GROUP]: columnsBySpreadsheet[spreadsheetId],
      },
    }));

    setAreGroupValueLabelsLoading(true);
    try {
      const response = await getSpreadsheetBulkData(clientId, { params });
      setGroupValueLabelsLookup(parseFormattedValuesResponse(response));
    }
    finally {
      setAreGroupValueLabelsLoading(false);
    }
  }, [charts, clientId, mapId]);

  useEffect(() => {
    fetchGroupValueLabels();
  }, [charts, clientId, fetchGroupValueLabels, mapId]);

  useEffect(() => {
    const itemsToRequest: BoundaryStatsItem[] = [];

    charts.forEach((chart) => {
      if (isChartBoundaryStatistics(chart)) {
        chart.metrics.forEach((metric) => {
          let itemData = null;
          if (isChartMetricSpreadsheetColumn(metric)) {
            itemData = getSpreadsheetColumnBoundaryStats({
              boundaryGroupId: chart.boundaryGroupId,
              spreadsheetColumnId: metric.column,
            });
          }
          if (isChartMetricLocationCount(metric)) {
            itemData = getLocationCountBoundaryStats({
              boundaryGroupId: chart.boundaryGroupId,
              column: metric.column,
              columnValue: metric.columnValue,
              spreadsheetId: metric.spreadsheetId,
            });
          }
          if (isChartMetricDemographic(metric)) {
            itemData = getDemographicBoundaryStats({
              boundaryGroupId: chart.boundaryGroupId,
              demographicId: metric.demographicId,
            });
          }
          if (itemData && !itemData.isCached) {
            itemsToRequest.push(itemData.item);
          }
        });
      }
    });

    dispatch(addBoundaryStatsItems(itemsToRequest));
  }, [charts, dispatch, getDemographicBoundaryStats, getLocationCountBoundaryStats, getSpreadsheetColumnBoundaryStats]);

  return {
    areGroupValueLabelsLoading,
    chartsStats,
    groupValueLabelsLookup,
  };
};
